import type { EngagementParticipantRole } from '@/__generated__/graphql';
import { ProjectMemberState, ProjectRole } from '@/__generated__/graphql';

import type { ViewerRole } from '../components/EngagementHub/roles';
import type { ChannelMemberView, EngagementView } from './viewModel';

/**
 * Naming people on live data.
 *
 * The read model gives ids, and four tables name them, richest first:
 *
 *  - `parties`, the project roster — only for a viewer who holds
 *    `project:view`, so always empty for the expert.
 *  - `expertIdentity` (ENG-5730) — the expert is never a project member.
 *  - `members`, the channel's recorded roster, which names OF staff: they are
 *    neither project members nor the expert, so before ENG-5439 a message from
 *    OnFrontiers rendered as the anonymous "A participant". Anyone in the
 *    roster who is not on the project is from OnFrontiers by construction — the
 *    join policy admits nobody else — which is what `isOnFrontiers` marks. It
 *    is a FLAG rather than a "· OnFrontiers" suffix welded into the name
 *    (ENG-5733): a suffix cannot be styled as the prototype's chip, and it
 *    followed the name onto the Participants card.
 *  - `customerIdentities` (ENG-5749) — every customer-side party by display
 *    name. The expert's only source for the customer side, when the
 *    customer's disclosure allows, and the customer side's for an approver who
 *    is not a project member (ENG-5750).
 *
 * An id none of them name gets a neutral label rather than a raw user id,
 * which is both ugly and a small leak.
 *
 * There is no "You". Every surface renders the same name for a person whoever
 * is looking, which is the convergence ENG-5733 asked for.
 */
interface PartyIdentityBase {
  readonly name: string;
  /** Their profile picture, for the avatar; null falls back to initials. */
  readonly pictureUrl: string | null;
}

/**
 * `isOnFrontiers` is PROVENANCE: this sender is from OnFrontiers. It is not
 * "show the chip" — the two came apart in ENG-5733 review. A roster member we
 * cannot name reads "OnFrontiers" already, so the chip is suppressed for them
 * (`nameIsBrand`) while the provenance stays true, which is what the message
 * bubble's treatment reads. Conflating the two styled that sender as a
 * customer.
 *
 * A union rather than two independent booleans, so "the name is the brand but
 * the sender is not from OnFrontiers" is a compile error instead of a rule
 * kept by hand: `nameIsBrand` only exists on the OnFrontiers arm.
 */
export type PartyIdentityView =
  | (PartyIdentityBase & { readonly isOnFrontiers: false; readonly nameIsBrand: false })
  | (PartyIdentityBase & { readonly isOnFrontiers: true; readonly nameIsBrand: boolean });

/**
 * Nothing names this id, but it is a real sender, so the row still renders.
 *
 * Handed out by reference to every unresolved id across all four surfaces, so
 * it is frozen and the fields are `readonly`: an in-place edit by one caller
 * would otherwise rewrite the fallback for every other one at once. Exported so
 * a caller with a better fallback can test for it by reference.
 */
export const UNKNOWN_PARTY: PartyIdentityView = Object.freeze({
  name: 'A participant',
  pictureUrl: null,
  isOnFrontiers: false,
  nameIsBrand: false,
});

/** Anyone not from OnFrontiers: a project member, the expert, the customer side. */
function plainParty(name: string, pictureUrl: string | null): PartyIdentityView {
  return { name, pictureUrl, isOnFrontiers: false, nameIsBrand: false };
}

/**
 * The project roster. Only for a viewer who holds `project:view`, so it never
 * names anyone to the expert.
 */
function fromProjectRoster(view: EngagementView, userId: string): PartyIdentityView | null {
  const known = view.parties.find((p) => p.userId === userId);
  return known ? plainParty(known.name, known.pictureUrl) : null;
}

/**
 * `expertIdentity` before the role label (ENG-5730): the expert is not a
 * project member, so `parties` never holds them and every activity line and
 * message they authored read as the anonymous "The expert". The label stays as
 * the fallback for an engagement whose expert has no name.
 */
function fromExpertIdentity(view: EngagementView, userId: string): PartyIdentityView | null {
  if (userId !== view.expertId) return null;
  return plainParty(
    view.expertIdentity?.name ?? 'The expert',
    view.expertIdentity?.pictureUrl ?? null
  );
}

/**
 * The channel roster, which is OF staff by construction. A rep we cannot name
 * IS the brand, so the name carries the mark and the chip would print
 * "OnFrontiers" twice on the same row. Trimmed rather than null-checked:
 * `identity.name` is `String!` on the wire but the api composes it from
 * first/last, so a nameless profile sends `''`.
 */
function fromChannelRoster(members: ChannelMemberView[], userId: string): PartyIdentityView | null {
  const recorded = members.find((m) => m.userId === userId);
  if (!recorded) return null;
  const named = recorded.name?.trim();
  return {
    name: named || 'OnFrontiers',
    pictureUrl: recorded.pictureUrl,
    isOnFrontiers: true,
    nameIsBrand: !named,
  };
}

/**
 * `customerIdentities`: the only table that names the customer side to the
 * expert (ENG-5749), and the one that names an approver who is on no project
 * to the customer side (ENG-5750). The mapper already dropped nameless entries.
 */
function fromCustomerIdentities(view: EngagementView, userId: string): PartyIdentityView | null {
  const customer = view.customerIdentities.find((p) => p.userId === userId);
  return customer ? plainParty(customer.name, customer.pictureUrl) : null;
}

/**
 * Resolve a user id to the name, picture and OnFrontiers mark every actor
 * surface renders, trying the tables in the order the module doc lists them.
 *
 * `customerIdentities` comes last of the four, so it only fills gaps: anyone
 * the project roster already named keeps that name, and a rep who is also
 * customer-side keeps the OnFrontiers chip from the channel roster.
 *
 * `self` is the last resort before UNKNOWN_PARTY and applies only to the
 * viewer's own id. It was threaded in for an account-level governance approver,
 * who is on no project and is not the expert: ENG-5733 removed the "You"
 * short-circuit that used to hide the gap (the prototype names every sender,
 * the viewer included). `customerIdentities` now names that approver to the
 * customer side (ENG-5750), so `self` covers a viewer no table carries — OF
 * staff on a surface called without the channel roster.
 */
export function partyIdentity(
  view: EngagementView,
  viewerUserId: string,
  members: ChannelMemberView[] = [],
  self: PartyIdentityView | null = null
): (userId: string) => PartyIdentityView | null {
  return (userId) => {
    // A system message has no sender. Distinct from a sender nothing resolves:
    // it gets a neutral placeholder and no name, never "A participant".
    if (!userId) return null;

    return (
      fromProjectRoster(view, userId) ??
      fromExpertIdentity(view, userId) ??
      fromChannelRoster(members, userId) ??
      fromCustomerIdentities(view, userId) ??
      (self && userId === viewerUserId ? self : UNKNOWN_PARTY)
    );
  };
}

export function partyNamer(
  view: EngagementView,
  viewerUserId: string,
  members: ChannelMemberView[] = [],
  self: PartyIdentityView | null = null
): (userId: string) => string {
  const identify = partyIdentity(view, viewerUserId, members, self);
  return (userId) => identify(userId)?.name ?? UNKNOWN_PARTY.name;
}

/**
 * The roster line the two sides read: who from OnFrontiers is in this
 * conversation. Empty until someone joins or speaks — staff read every channel
 * without a row, so silence here means nothing about who is watching, which is
 * the whole point of the design.
 */
export function channelRosterLabel(
  members: ChannelMemberView[],
  viewerUserId: string
): string | null {
  const others = members.filter((m) => m.userId !== viewerUserId);
  if (others.length === 0) return null;

  const names = others.map((m) => m.name ?? 'Someone from OnFrontiers');

  return names.length === 1
    ? `${names[0]} joined this conversation`
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} joined this conversation`;
}

/*
 * ─── The Participants roster ────────────────────────────────────────────────
 *
 * Labels and ordering, kept beside the function that composes them. Both
 * mirror Brian's prototype (`components/EngagementHub/OverviewTab.tsx`).
 */

/**
 * Exhaustive over `EngagementParticipantRole` on purpose: adding a role to the
 * api now fails this file at compile time instead of quietly reaching the card
 * as a raw `snake_case` wire value.
 */
const ROSTER_ROLE_LABEL: Record<EngagementParticipantRole, string> = {
  governance_approver: 'Governance approver',
  work_approver: 'Work approver',
  watcher: 'Watcher',
};

const PROJECT_MEMBER_LABEL = 'Project member';

/**
 * Prototype rank, used both to order rows and to order the labels inside one.
 * `Watcher` has no prototype equivalent and sorts last; so does any label the
 * exhaustive map above has not caught up with yet.
 */
const ROLE_RANK = ['Work approver', 'Governance approver', PROJECT_MEMBER_LABEL, 'Watcher'];

const rank = (label: string) => {
  const i = ROLE_RANK.indexOf(label);
  return i === -1 ? ROLE_RANK.length : i;
};

export interface RosterRowView {
  userId: string;
  /** At least one, in prototype rank order. */
  roles: string[];
}

/**
 * The Participants card, as one row per person rather than one row per role.
 *
 * `Engagement.participants` is keyed (user, role), so a team owner who is also
 * the project owner arrives as two rows and used to render as two people
 * (ENG-5738). The governance approver defaults to the team owner and the work
 * approver to the project owner, so that pairing is ordinary, not an edge case.
 *
 * Brian's prototype already composes it this way — `partyOrder` in
 * `components/EngagementHub/OverviewTab.tsx` carries a `roles: string[]` per
 * user and `PartyRow` joins them — so this is the live twin of that, including
 * its rank order.
 *
 * The expert is excluded: they are composed separately and lead the card
 * (ENG-5730), with an avatar and a profile link the approver rows do not have.
 *
 * Returns **null**, not an empty array, when the viewer is not entitled to the
 * roster. `participants` carries that distinction on the wire (null means
 * denied, `[]` means genuinely empty), and collapsing the two here would hand a
 * caller project-member rows on a view where the api refused to say who the
 * parties are. Ordering within a rank is the api's own order, which `Map` and
 * `Set` insertion order plus a stable `sort` preserve.
 */
export function engagementRoster(view: EngagementView): RosterRowView[] | null {
  if (view.participants === null) return null;

  const byUser = new Map<string, Set<string>>();
  const add = (userId: string, label: string) => {
    if (userId === view.expertId) return;
    const held = byUser.get(userId) ?? new Set<string>();
    held.add(label);
    byUser.set(userId, held);
  };

  for (const p of view.participants) add(p.userId, ROSTER_ROLE_LABEL[p.role] ?? p.role);

  // Project members round the card out to "who is involved" rather than "who
  // approves", which is what the prototype's fourth rank is for.
  //
  // Both halves of the gate are load-bearing, because `view.parties` is the
  // project ROSTER, which is wider than the project MEMBERS in two directions:
  //
  //  - Role. The prototype derives `project_member_user_ids` as
  //    `members.filter(role === 'member')` (`types.ts:225`) and keeps owners in
  //    a separate `project_owner_user_ids` that feeds work-approver selection,
  //    so an owner, manager, associate or viewer holding no engagement role has
  //    no label there and never reaches the card.
  //  - State. An invited (`awaiting_approval`) or `denied` member still arrives
  //    with a `user`, and presenting them as a participant would be a claim the
  //    project itself does not make. `ProjectPreview` filters the same way.
  //
  // Deliberately only as a SOLE label: `deriveUserRoleLabels` appends it
  // unconditionally, but that rule is never exercised — in every prototype
  // scene the approvers and the project members are disjoint sets. In live
  // data the project owner is the work approver by default, so appending it
  // would ride on nearly every row and say nothing about what that person can
  // do on this engagement.
  for (const party of view.parties)
    if (
      party.projectRole === ProjectRole.Member &&
      party.projectState === ProjectMemberState.Active &&
      !byUser.has(party.userId)
    )
      add(party.userId, PROJECT_MEMBER_LABEL);

  return [...byUser.entries()]
    .map(([userId, held]) => ({
      userId,
      roles: [...held].sort((a, b) => rank(a) - rank(b)),
    }))
    .sort((a, b) => rank(a.roles[0]) - rank(b.roles[0]));
}

/**
 * The Hub heading names the counterparty: the customer organization for the
 * expert, the expert for the customer side. Falls back to a neutral title only
 * when the other side genuinely has no name to give.
 *
 * The customer-side arm reads `expertIdentity`, not `parties` (ENG-5730). It
 * used to look the expert up in the project roster, which the expert is never
 * on — so the fallback fired on every ordinary engagement and the detail page
 * named the expert nowhere at all. `expertIdentity` is the api field for
 * exactly this: the expert's display identity, visible to every party.
 *
 * The expert arm is the same defect mirrored (ENG-5749): it read
 * `project.organizationName`, which rides on the `project:view`-filtered
 * `primaryProject` the expert never receives, so it always said "Engagement".
 * `customerOrganizationName` is the ungated twin.
 */
export function counterpartyHeading(view: EngagementView, viewerRole: ViewerRole): string {
  if (viewerRole === 'expert') return view.customerOrganizationName ?? 'Engagement';
  return view.expertIdentity?.name ?? 'Engagement';
}
